import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import ReactCountryFlag from "react-country-flag";

import {
  Button,
  Checkbox,
  Icon,
  IconButton,
  Input,
  Modal,
  Money,
  NumberInput,
  Segmented,
  Select,
} from "../../components/ui";
import { regionFlag, regionLabel } from "./arbitrageRegions.js";
import { SEQUENCES_URL, preferWeights } from "./arbitrageSequencesApi.js";
import "./arbitrage-sequences.scss";

/**
 * Arbitrage Sequences (Alex, 2026-10-19): a saved sourcing brief that keeps
 * its own list of matching watches. Four blocks — brand tiers, one sort,
 * ranked filters, actions. A MUST filter removes watches that fail it; a
 * PREFER adds weight by position (first counts most). Every filter shows its
 * data health: a Must on broken data pauses the sequence, a Prefer is left out.
 *
 * Read top to bottom, a sequence is a pipeline, so it is drawn as one: the
 * builder is a rail of four numbered stages with a live preview docked
 * under it, and a sequence's tab opens on a command header whose funnel
 * shows how many watches survive each stage tonight.
 *
 * The rules live in ArbitrageSequenceService; this file only edits and shows.
 */

const TIER_LABEL = { core: "Core brands", independent: "Independent", rest: "Rest" };

const ROUTE_TYPE_OPTIONS = [
  { value: "trade", label: "Trade → Trade" },
  { value: "market", label: "Market → Market" },
  { value: "cross", label: "Trade ↔ Market" },
];

const REGIONS = ["uk", "europe", "usa", "middle-east", "asia"];

const POOL_LABEL = { trade: "Trade", market: "Market" };

/** The unit shown after a filter's number. */
const UNIT = {
  high_potential: "score",
  high_gap: "%",
  profit_holds: "%",
  sells_fast: "days",
  buyers_waiting: "leads",
  low_trade_supply: "× usual",
  low_market_supply: "× usual",
};

const MODE_OPTIONS = [
  { value: "must", label: "Must" },
  { value: "prefer", label: "Prefer" },
];

/** The funnel's stages, in the order the server applies them. */
const FUNNEL = [
  { key: "window", label: "With a route" },
  { key: "tier", label: "Brand tier" },
  { key: "route_musts", label: "Price & route musts" },
  { key: "reference_musts", label: "Watch musts" },
  { key: "matched", label: "Matched" },
];

/** One hue per prefer position, shared by the weight bar and the card checklist. */
const PREFER_HUES = ["#8b7bff", "#4fd1c5", "#f6ad55", "#f687b3", "#63b3ed", "#9ae6b4"];

const PREVIEW_DEBOUNCE_MS = 500;

const blank = (catalogue) => ({
  name: "",
  brand_groups: ["core"],
  sort: catalogue?.sorts?.[0]?.key || "lowest_buy",
  months: "3",
  filters: [],
  actions: [],
});

const fromSequence = (s) => ({
  name: s.name,
  brand_groups: s.brand_groups,
  sort: s.sort,
  months: String(s.months),
  filters: s.filters,
  actions: s.actions,
});

const labelsOf = (catalogue) => Object.fromEntries((catalogue?.filters || []).map((f) => [f.key, f.label]));

const clock = (stamp) => (stamp ? String(stamp).slice(11, 16) : null);

/** Bar length on a square-root scale, so a stage of 14 is still visible next to 2,400. */
const funnelWidth = (n, max) => (max > 0 && n > 0 ? Math.max(4, Math.sqrt(n / max) * 100) : 0);

const Pulse = ({ state }) => <i className={`seq-pulse is-${state === "paused" ? "paused" : "live"}`} aria-hidden="true" />;

/** The tab label: a status pulse and the name. */
export const SequenceTabLabel = ({ sequence }) => (
  <span className="seq-tab">
    <Pulse state={sequence.status?.state} />
    {sequence.name}
  </span>
);

/** Prefer weights as one stacked bar, each segment in its position's hue. */
const WeightBar = ({ prefers, weights, labels }) =>
  prefers.length ? (
    <div className="seq-weights" role="img"
      aria-label={prefers.map((f) => `${labels[f.key] || f.key} ${weights[f.key]}%`).join(", ")}>
      {prefers.map((f, i) => (
        <span key={f.key} style={{ flexGrow: weights[f.key] || 0, "--hue": PREFER_HUES[i % PREFER_HUES.length] }}>
          <b>{weights[f.key]}%</b> {labels[f.key] || f.key}
        </span>
      ))}
    </div>
  ) : null;

/** How many watches survive each stage. */
const Funnel = ({ funnel, compact = false }) => {
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setDrawn(true));
    return () => window.cancelAnimationFrame(id);
  }, []);
  if (!funnel) return null;
  const max = funnel.window || 0;
  return (
    <ol className={`seq-funnel${compact ? " is-compact" : ""}`} aria-label="Watches left after each stage">
      {FUNNEL.map((stage) => {
        const n = funnel[stage.key] ?? 0;
        return (
          <li key={stage.key} className={stage.key === "matched" ? "is-final" : ""}>
            <span className="seq-funnel__label">{stage.label}</span>
            <span className="seq-funnel__track">
              <i style={{ width: drawn ? `${funnelWidth(n, max)}%` : 0 }} />
            </span>
            <span className="seq-funnel__n">{n.toLocaleString()}</span>
          </li>
        );
      })}
    </ol>
  );
};

const FilterParam = ({ meta, filter, onChange, currency }) => {
  if (meta.param === "value") {
    return (
      <NumberInput
        size="md"
        value={filter.value ?? meta.default}
        onChange={(value) => onChange({ value })}
        money={filter.key === "min_profit"}
        currency={filter.key === "min_profit" ? currency : undefined}
        suffix={UNIT[filter.key]}
        min={0}
        step={filter.key.startsWith("low_") ? 0.1 : 1}
        decimals={filter.key.startsWith("low_") ? 1 : undefined}
        aria-label={`${meta.label} value`}
      />
    );
  }
  if (meta.param === "range") {
    return (
      <span className="seq-range">
        <NumberInput size="md" money currency={currency} value={filter.min ?? null}
          onChange={(min) => onChange({ min })} placeholder="From" aria-label="From price" />
        <span aria-hidden="true">–</span>
        <NumberInput size="md" money currency={currency} value={filter.max ?? null}
          onChange={(max) => onChange({ max })} placeholder="To" aria-label="To price" />
      </span>
    );
  }
  if (meta.param === "values") {
    const options = filter.key === "route_types"
      ? ROUTE_TYPE_OPTIONS
      : REGIONS.map((r) => ({ value: r, label: regionLabel(r) }));
    return (
      <Select label={meta.label} options={options} value={filter.values || []} multi clearable
        onChange={(values) => onChange({ values: values || [] })} placeholder="Choose…" />
    );
  }
  return null;
};

/**
 * What the draft would match, asked of the server as the form changes
 * (debounced, the previous request cancelled). Nothing is saved.
 */
const useLivePreview = (open, storeIdM, form) => {
  const [state, setState] = useState({ loading: false, data: null, error: false });
  const body = JSON.stringify({ ...form, name: undefined, months: Number(form.months) });

  useEffect(() => {
    if (!open || !form.brand_groups.length) return undefined;
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true }));
    const timer = window.setTimeout(async () => {
      try {
        const { data } = await axios.post(`${SEQUENCES_URL}/preview`, JSON.parse(body), {
          params: { store_id_m: storeIdM },
          signal: controller.signal,
        });
        setState({ loading: false, data, error: false });
      } catch (e) {
        if (!controller.signal.aborted) setState({ loading: false, data: null, error: true });
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [open, storeIdM, body, form.brand_groups.length]);

  return state;
};

const PreviewDock = ({ preview, labels }) => {
  const { loading, data, error } = preview;
  const paused = data?.status?.state === "paused";
  return (
    <section className={`seq-dock${loading ? " is-loading" : ""}${paused ? " is-paused" : ""}`} aria-live="polite">
      <header className="seq-dock__head">
        <span className="seq-dock__eyebrow"><Icon name="Sparkles" size="sm" /> Live preview</span>
        <span className="seq-dock__total">
          {error ? "—" : data ? (paused ? "Paused" : data.total.toLocaleString()) : "…"}
          {data && !paused && <small> watch{data.total === 1 ? "" : "es"} tonight</small>}
        </span>
      </header>
      {error && <p className="seq-muted">Couldn't preview this sequence. It will still save.</p>}
      {paused && (
        <ul className="seq-reasons">
          {data.status.reasons.map((r) => <li key={r}>{r}</li>)}
        </ul>
      )}
      {data && !paused && (
        <>
          <Funnel funnel={data.status?.funnel} compact />
          {data.top?.length > 0 && (
            <ol className="seq-dock__top">
              {data.top.map((w) => (
                <li key={w.product_id}>
                  <span className="seq-dock__ref">{w.reference}</span>
                  <span className="seq-dock__name">{w.brand} · {w.model}</span>
                  <span className="seq-dock__meta">
                    {w.met?.length ? w.met.map((k) => labels[k] || k).join(" · ") : "Musts only"}
                  </span>
                  {w.profit != null && (
                    <Money value={w.profit} currency={data.base_currency} decimals={0} size="sm" tone="success" />
                  )}
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </section>
  );
};

/** The builder, in a drawer. `sequence` null = new. */
export const SequenceBuilder = ({ open, onClose, storeIdM, catalogue, sequence, currency, onSaved, onDeleted }) => {
  const [form, setForm] = useState(() => blank(catalogue));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setForm(sequence ? fromSequence(sequence) : blank(catalogue));
      setError(null);
    }
  }, [open, sequence, catalogue]);

  const preview = useLivePreview(open, storeIdM, form);
  const filterMeta = useMemo(() => Object.fromEntries((catalogue?.filters || []).map((f) => [f.key, f])), [catalogue]);
  const labels = useMemo(() => labelsOf(catalogue), [catalogue]);
  // An unhealthy Prefer is left out by the server, so it gets no share here either.
  const counted = form.filters.filter((f) => f.mode === "must" || filterMeta[f.key]?.health?.ok !== false);
  const weights = preferWeights(counted);
  const prefers = counted.filter((f) => f.mode === "prefer");
  const unused = (catalogue?.filters || []).filter((f) => !form.filters.some((x) => x.key === f.key));
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setFilter = (i, patch) => set({ filters: form.filters.map((f, k) => (k === i ? { ...f, ...patch } : f)) });
  const move = (i, d) => {
    const next = [...form.filters];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    set({ filters: next });
  };
  const hasAction = (key) => form.actions.some((a) => a.type === key);
  const toggleAction = (key, on) =>
    set({ actions: on ? [...form.actions, { type: key, recipients: null }] : form.actions.filter((a) => a.type !== key) });

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = { ...form, months: Number(form.months) };
      const params = { store_id_m: storeIdM };
      const { data } = sequence
        ? await axios.put(`${SEQUENCES_URL}/${sequence.id}`, body, { params })
        : await axios.post(SEQUENCES_URL, body, { params });
      onSaved(data);
    } catch (e) {
      const errors = e?.response?.data?.errors;
      setError(errors ? Object.values(errors).flat()[0] : e?.response?.data?.message || "Couldn't save the sequence.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await axios.delete(`${SEQUENCES_URL}/${sequence.id}`, { params: { store_id_m: storeIdM } });
      onDeleted(sequence.id);
    } catch {
      setError("Couldn't delete the sequence.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="drawer"
      size="lg"
      title={sequence ? "Edit sequence" : "New sequence"}
      className="seq-panel"
      footer={
        <div className="seq-footer">
          {sequence && (
            <Button variant="ghost" leadingIcon={<Icon name="Trash2" size="sm" />} onClick={remove} disabled={saving}>
              Delete
            </Button>
          )}
          <span className="seq-footer__live" aria-live="polite">
            <Pulse state={preview.data?.status?.state} />
            {preview.data?.status?.state === "paused" ? "Paused" : preview.data ? <><b>{preview.data.total.toLocaleString()}</b> tonight</> : "…"}
          </span>
          <span className="seq-footer__spacer" />
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} loading={saving} disabled={!form.name.trim() || !form.brand_groups.length}>
            Save
          </Button>
        </div>
      }
    >
      <div className="seq-form">
        <label className="seq-name">
          <span>Name</span>
          <Input value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Top Brands · Cash-flow" maxLength={120} />
        </label>

        <div className="seq-rail">
          <section className="seq-stage">
            <h3><b>1</b> Brand tier</h3>
            <div className="seq-chips" role="group" aria-label="Brand tiers">
              {(catalogue?.brand_groups || []).map((g) => {
                const on = form.brand_groups.includes(g);
                return (
                  <button key={g} type="button" className="seq-chip" aria-pressed={on}
                    onClick={() => set({ brand_groups: on ? form.brand_groups.filter((x) => x !== g) : [...form.brand_groups, g] })}>
                    {TIER_LABEL[g] || g}
                  </button>
                );
              })}
            </div>
          </section>

          <section className="seq-stage">
            <h3><b>2</b> Sort <small>only one</small></h3>
            <div className="seq-stage__row">
              <Select
                label="Sort"
                options={(catalogue?.sorts || []).map((s) => ({ value: s.key, label: s.label, hint: s.health?.ok === false ? "data issue" : undefined }))}
                value={form.sort}
                onChange={(sort) => sort && set({ sort })}
              />
              <Segmented size="sm" label="Data period" value={form.months}
                options={(catalogue?.windows || [1, 3, 6, 12]).map((m) => ({ value: String(m), label: `${m}M` }))}
                onChange={(months) => set({ months })} />
            </div>
          </section>

          <section className="seq-stage">
            <h3><b>3</b> Filters <small>order sets weight</small></h3>
            <WeightBar prefers={prefers} weights={weights} labels={labels} />
            {form.filters.length === 0 && <p className="seq-muted">No filters: every watch in the tiers matches, ranked by the sort.</p>}
            <ol className="seq-filters">
              {form.filters.map((filter, i) => {
                const meta = filterMeta[filter.key];
                if (!meta) return null;
                const sick = meta.health && meta.health.ok === false;
                const hue = filter.mode === "prefer" && !sick
                  ? PREFER_HUES[prefers.findIndex((p) => p.key === filter.key) % PREFER_HUES.length]
                  : undefined;
                return (
                  <li key={filter.key} className={`seq-filter${sick ? " is-sick" : ""} is-${filter.mode}`} style={hue ? { "--hue": hue } : undefined}>
                    <span className="seq-filter__pos">{i + 1}</span>
                    <div className="seq-filter__main">
                      <div className="seq-filter__top">
                        <b>{meta.label}</b>
                        <span className="seq-filter__weight">
                          {filter.mode === "must" ? "Must" : sick ? "Off" : `${weights[filter.key]}%`}
                        </span>
                      </div>
                      <div className="seq-filter__controls">
                        <Segmented size="sm" label={`${meta.label}: must or prefer`} options={MODE_OPTIONS}
                          value={filter.mode} onChange={(mode) => setFilter(i, { mode })} />
                        <FilterParam meta={meta} filter={filter} currency={currency} onChange={(patch) => setFilter(i, patch)} />
                      </div>
                      {sick && (
                        <p className="seq-filter__health">
                          <Icon name="AlertTriangle" size="sm" />
                          {filter.mode === "must" ? "Pauses this sequence: " : "Left out for now: "}
                          {meta.health.message}
                        </p>
                      )}
                    </div>
                    <span className="seq-filter__move">
                      <IconButton size="sm" variant="ghost" label={`Move ${meta.label} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                        <Icon name="ChevronUp" size="sm" />
                      </IconButton>
                      <IconButton size="sm" variant="ghost" label={`Move ${meta.label} down`} disabled={i === form.filters.length - 1} onClick={() => move(i, 1)}>
                        <Icon name="ChevronDown" size="sm" />
                      </IconButton>
                      <IconButton size="sm" variant="ghost" label={`Remove ${meta.label}`}
                        onClick={() => set({ filters: form.filters.filter((_, k) => k !== i) })}>
                        <Icon name="X" size="sm" />
                      </IconButton>
                    </span>
                  </li>
                );
              })}
            </ol>
            {unused.length > 0 && (
              <Select
                label="Add a filter"
                options={unused.map((f) => ({ value: f.key, label: f.label, hint: f.health?.ok === false ? "data issue" : undefined }))}
                value=""
                placeholder="+ Add a filter"
                onChange={(key) => {
                  const meta = filterMeta[key];
                  if (!meta) return;
                  set({ filters: [...form.filters, { key, mode: "prefer", ...(meta.param === "value" ? { value: meta.default } : {}) }] });
                }}
              />
            )}
          </section>

          <section className="seq-stage">
            <h3><b>4</b> Action <small>when a watch enters</small></h3>
            <ul className="seq-actions">
              {(catalogue?.actions || []).map((a) => (
                <li key={a.key}>
                  <Checkbox checked={hasAction(a.key)} onChange={(e) => toggleAction(a.key, e.target.checked)} label={a.label} />
                  <span className="seq-soon">Coming soon</span>
                  {a.key === "email" && hasAction("email") && (
                    <Input
                      value={form.actions.find((x) => x.type === "email")?.recipients || ""}
                      onChange={(e) => set({ actions: form.actions.map((x) => (x.type === "email" ? { ...x, recipients: e.target.value } : x)) })}
                      placeholder="Who to email"
                      aria-label="Who to email"
                    />
                  )}
                </li>
              ))}
            </ul>
          </section>
        </div>

        <PreviewDock preview={preview} labels={labels} />

        {error && <p className="seq-error" role="alert">{error}</p>}
      </div>
    </Modal>
  );
};

/** A sequence's command header: live or paused, tonight's movement, the funnel. */
export const SequenceCommand = ({ sequence, status, total, catalogue, onEdit }) => {
  const labels = labelsOf(catalogue);
  const paused = status?.state === "paused";
  const sortLabel = (catalogue?.sorts || []).find((s) => s.key === sequence.sort)?.label || sequence.sort;
  const prefers = (sequence.filters || []).filter((f) => f.mode === "prefer" && !(status?.ignored || []).includes(f.key));
  const weights = preferWeights(prefers);
  const musts = (sequence.filters || []).filter((f) => f.mode === "must");
  const count = total ?? sequence.match_count;

  return (
    <section className={`seq-command${paused ? " is-paused" : ""}`}>
      <div className="seq-command__head">
        <div className="seq-command__title">
          <span className={`seq-state is-${paused ? "paused" : "live"}`}>
            <Pulse state={status?.state} />
            {paused ? "Paused" : "Live"}
          </span>
          <h2>{sequence.name}</h2>
          <p>
            {sequence.brand_groups.map((g) => TIER_LABEL[g] || g).join(" + ")} · {sequence.months}M · {sortLabel}
            {sequence.evaluated_at && <> · checked {clock(sequence.evaluated_at)}</>}
          </p>
        </div>
        <Button variant="secondary" size="md" leadingIcon={<Icon name="Pencil" size="sm" />} onClick={onEdit}>Edit</Button>
      </div>

      {paused ? (
        <ul className="seq-reasons">
          {(status.reasons || []).map((r) => <li key={r}>{r}</li>)}
        </ul>
      ) : (
        <div className="seq-command__body">
          <dl className="seq-kpis">
            <div>
              <dt>In sequence</dt>
              <dd>{count.toLocaleString()}</dd>
            </div>
            <div className="is-up">
              <dt>Entered · 24h</dt>
              <dd>+{(sequence.entered_24h || 0).toLocaleString()}</dd>
            </div>
            <div className="is-down">
              <dt>Left · 24h</dt>
              <dd>−{(sequence.exited_24h || 0).toLocaleString()}</dd>
            </div>
          </dl>
          <Funnel funnel={status?.funnel} />
        </div>
      )}

      {!paused && (prefers.length > 0 || musts.length > 0) && (
        <div className="seq-command__rules">
          {musts.length > 0 && (
            <span className="seq-musts">
              Must: {musts.map((f) => labels[f.key] || f.key).join(" · ")}
            </span>
          )}
          <WeightBar prefers={prefers} weights={weights} labels={labels} />
        </div>
      )}
      {!paused && status?.ignored?.length > 0 && (
        <p className="seq-muted">Left out until their data is healthy: {status.ignored.map((k) => labels[k] || k).join(", ")}.</p>
      )}
    </section>
  );
};

const isNew = (enteredAt) => enteredAt && Date.now() - new Date(String(enteredAt).replace(" ", "T")).getTime() < 86400000;

/**
 * On a card: the match score as a ring, each preference met or not (in its
 * position's hue), and the figures behind the watch-level filters.
 */
export const SequenceMatch = ({ match, prefers = [], catalogue, currency }) => {
  const labels = labelsOf(catalogue);
  const f = match.facts || {};
  const score = Math.round(match.match_score || 0);
  const facts = [
    f.ch24_in_stock > 0 && `${f.ch24_in_stock} in stock on Chrono24`,
    f.ch24_private > 0 && `${f.ch24_private} from private sellers`,
    f.trade_ratio != null && `Trade supply ${f.trade_ratio}× usual`,
    f.market_ratio != null && `Marketplace supply ${f.market_ratio}× usual`,
  ].filter(Boolean);
  const flag = f.cheapest_live_region ? regionFlag(f.cheapest_live_region) : null;

  return (
    <div className="seq-match">
      <div className="seq-match__row">
        {prefers.length > 0 && (
          <span className="seq-ring" style={{ "--p": score }} role="img" aria-label={`Match ${score}%`}>
            <span>{score}</span>
          </span>
        )}
        <ul className="seq-checks">
          {prefers.map((p, i) => {
            const met = match.met.includes(p.key);
            return (
              <li key={p.key} className={met ? "is-met" : ""} style={{ "--hue": PREFER_HUES[i % PREFER_HUES.length] }}>
                <Icon name={met ? "Check" : "X"} size="sm" />
                {labels[p.key] || p.key}
              </li>
            );
          })}
        </ul>
        {isNew(match.entered_at) && <span className="seq-new">New</span>}
      </div>
      {(facts.length > 0 || f.cheapest_live != null) && (
        <p className="seq-match__facts">
          {f.cheapest_live != null && (
            <span className="seq-cheapest">
              Cheapest live <Money value={f.cheapest_live} currency={currency} decimals={0} size="sm" />
              {f.cheapest_live_region && (
                <>
                  {" · "}
                  {flag && <ReactCountryFlag countryCode={flag} svg className="arb-flag" aria-hidden="true" />}
                  {POOL_LABEL[f.cheapest_live_pool] || ""} {regionLabel(f.cheapest_live_region)}
                </>
              )}
            </span>
          )}
          {facts.map((t) => <span key={t}>{t}</span>)}
        </p>
      )}
    </div>
  );
};
